/**
 * @file 试卷分页测高
 * @description 等待字体与图片后读取内容高度，并计算 A4 内容区可用高度。
 *
 * @whenToUse 打印模板按页塞入题目时。
 * @whenNotToUse 普通列表布局。
 */

import type { PaperBlock } from '@/components/pages/handout-page/print/blocks.tsx'

const FALLBACK_CONTAINER_HEIGHT_PX = Math.floor((248 * 96) / 25.4)
const CONTAINER_HEIGHT_SAFETY_PX = 2

/**
 * 等待一帧布局。
 */
function waitAnimationFrame(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => resolve())
  })
}

/**
 * 等待文档字体就绪。
 */
export async function waitForDocumentFontsReady(): Promise<void> {
  try {
    await document.fonts.ready
  } catch {
    // 字体 API 不可用时继续测高
  }
}

/**
 * 等待容器内图片加载完成。
 * @param el 容器
 */
export async function waitForElementImagesLoaded(el?: HTMLElement | null): Promise<void> {
  if (!el) return
  const images = Array.from(el.querySelectorAll('img'))
  await Promise.all(
    images.map(async (img) => {
      if (!img.complete) {
        await new Promise<void>((resolve) => {
          const done = () => resolve()
          img.addEventListener('load', done, { once: true })
          img.addEventListener('error', done, { once: true })
        })
      }
      try {
        await img.decode()
      } catch {
        // decode 失败不影响继续测高
      }
    }),
  )
}

/**
 * 读取节点实际高度。
 * @param el 节点
 */
export function getElementActualHeight(el?: HTMLElement | null): number {
  if (!el) return 0
  return Math.ceil(
    Math.max(el.getBoundingClientRect().height, el.offsetHeight, el.scrollHeight),
  )
}

/**
 * 字体与图片稳定后再测高。
 * @param el 内容容器
 */
export async function measureStableElementHeight(el?: HTMLElement | null): Promise<number> {
  await waitForDocumentFontsReady()
  await waitForElementImagesLoaded(el)
  await waitAnimationFrame()
  await waitAnimationFrame()
  return getElementActualHeight(el)
}

/**
 * 从 HTML 中收集图片地址。
 * @param html 富文本
 */
function imageSourcesFromHtml(html?: string): string[] {
  if (!html) return []
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return Array.from(doc.querySelectorAll('img'))
    .map((img) => img.getAttribute('src') || '')
    .filter(Boolean)
}

/**
 * 预加载分页块里的图片，避免测高时尚未占位。
 * @param block 分页块
 */
export async function waitForBlockImagesLoaded(block: PaperBlock): Promise<void> {
  const sources = new Set<string>()
  const data = block.data
  if (data) {
    for (const html of [data.question, data.exampleHtml, data.analysisHtml, data.answerHtml]) {
      imageSourcesFromHtml(html).forEach((src) => sources.add(src))
    }
    data.options?.forEach((option) => {
      imageSourcesFromHtml(option.valueHtml).forEach((src) => sources.add(src))
    })
    if (data.qrCodeDataUrl) sources.add(data.qrCodeDataUrl)
  }
  block.items?.forEach((item) => {
    imageSourcesFromHtml(item.html).forEach((src) => sources.add(src))
    item.options?.forEach((option) => {
      imageSourcesFromHtml(option.valueHtml).forEach((src) => sources.add(src))
    })
  })
  await Promise.all(
    [...sources].map(
      (src) =>
        new Promise<void>((resolve) => {
          const img = new Image()
          const done = () => resolve()
          img.onload = done
          img.onerror = done
          img.src = src
        }),
    ),
  )
}

/**
 * 测量当前模板内容区可用高度。
 * @param root 模板根节点
 */
export async function resolvePaperQuestionContainerHeight(
  root?: HTMLElement | null,
): Promise<number> {
  await waitForDocumentFontsReady()
  for (let i = 0; i < 30; i += 1) {
    const container = root?.querySelector('.paper-question-container')
    const height = container instanceof HTMLElement ? container.offsetHeight : 0
    if (height > 0) return Math.max(0, height - CONTAINER_HEIGHT_SAFETY_PX)
    await new Promise((resolve) => setTimeout(resolve, 16))
  }
  return Math.max(0, FALLBACK_CONTAINER_HEIGHT_PX - CONTAINER_HEIGHT_SAFETY_PX)
}

/**
 * 去掉分页过程中的空页。
 * @param pages 页面块
 */
export function pruneEmptyPaperPages(pages: PaperBlock[][]): PaperBlock[][] {
  const pruned = pages.filter((page) => page.length > 0)
  return pruned.length > 0 ? pruned : [[]]
}
